import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import { useChecklist } from '../context/ChecklistContext';
import { useFilters } from '../context/FilterContext';
import TraceLabel from '../components/trace/TraceLabel';
import { 
  CheckSquare, FileText, Plus,
  Edit, Eye, MoreVertical, Copy, Archive, FileDown, Loader2, Circle
} from 'lucide-react';
import { plants } from '../data/mockData';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDate } from '../data/mockData';
import TemplateCreateDialog from '../components/checklists/TemplateCreateDialog';
import TemplateViewerDrawer from '../components/checklists/TemplateViewerDrawer';
import ChecklistBuilderPanel from '../components/checklists/ChecklistBuilderPanel';
import ExecutionsTable from '../components/checklists/ExecutionsTable';
import ReportSection from '../components/export/ReportSection';

const ChecklistsPage = () => {
  const { pidTraceMode } = useTrace();
  const { isReadOnly, canEdit, currentRole } = useApp();
  const { plant } = useFilters();
  const { 
    templates, 
    executions, 
    duplicateTemplate, 
    retireTemplate,
    resetDemoData 
  } = useChecklist();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [activeTab, setActiveTab] = useState('library');
  const [isGeneratingCXO, setIsGeneratingCXO] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  // Get current plant name
  const currentPlantName = plants.find(p => p.id === plant)?.name || 'Plant A';

  // Sync tab with URL
  React.useEffect(() => {
    if (location.pathname.includes('/builder')) {
      setActiveTab('builder');
    } else if (location.pathname.includes('/executions')) {
      setActiveTab('executions');
    } else {
      setActiveTab('library');
    }
  }, [location.pathname]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Effective': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Draft': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Retired': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'QA Review': return 'bg-blue-50 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const handleCreateSuccess = (template) => {
    setSelectedTemplateId(template.id);
    setActiveTab('builder');
    navigate('/checklists/builder');
  };

  const handleView = (template) => {
    setSelectedTemplate(template);
    setViewerOpen(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplateId(template.id);
    setActiveTab('builder');
    navigate('/checklists/builder');
  };

  const handleDuplicate = (templateId) => {
    const newTemplate = duplicateTemplate(templateId);
    if (newTemplate) {
      setSelectedTemplateId(newTemplate.id);
      setActiveTab('builder');
      navigate('/checklists/builder');
    }
  };

  const handleRetire = (templateId) => {
    retireTemplate(templateId);
  };

  const handleStartExecution = (execution) => {
    // Navigate to iPad execution if available, or show preview
    if (execution.taskId) {
      navigate(`/tasks/${execution.taskId}/execute`);
    } else {
      // Show preview modal (handled by ExecutionsTable)
    }
  };

  const handleGenerateCXOReport = async () => {
    setIsGeneratingCXO(true);
    
    const progressMessages = [
      'Reading checklist data...',
      'Checking compliance metrics...',
      'Analyzing execution status...',
      'Compiling executive summary...',
      'Opening report...'
    ];

    // Show progress messages with delays
    for (let i = 0; i < progressMessages.length; i++) {
      setProgressMessage(progressMessages[i]);
      await new Promise(resolve => setTimeout(resolve, i === progressMessages.length - 1 ? 1000 : 800));
    }

    try {
      // Open the SharePoint link in a new tab
      const reportUrl = 'https://myswishclub-my.sharepoint.com/:b:/g/personal/gourab_paul_swishclub_in/IQBIIxdSQE-NQ4gC9pcSyWgYAe1O4X23VXg52wKTPbZPPH8?e=lff3qf';
      window.open(reportUrl, '_blank', 'noopener,noreferrer');

      setIsGeneratingCXO(false);
      setProgressMessage('');
    } catch (error) {
      console.error('Error opening CXO report:', error);
      setIsGeneratingCXO(false);
      setProgressMessage('');
      alert('Failed to open report. Please try again.');
    }
  };

  return (
    <div className="space-y-6" data-testid="checklists-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Checklists
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Build, manage, and track GMP checklist executions
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {!isReadOnly && (
            <Button 
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white" 
              data-testid="new-checklist"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Checklist Template
            </Button>
          )}
        </div>
      </div>

      {/* CXO Report Generation Dialog */}
      <Dialog open={isGeneratingCXO} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generating CXO Compliance Readiness Report</DialogTitle>
            <DialogDescription>
              Please wait while we compile your compliance report...
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#fd4816] mb-4" />
            <p className="text-sm text-slate-600 font-medium">{progressMessage}</p>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        if (v === 'builder') navigate('/checklists/builder');
        else if (v === 'executions') navigate('/checklists/executions');
        else navigate('/checklists/library');
      }}>
        <TabsList className="bg-slate-100 border border-slate-200">
          {!isReadOnly && (
            <TabsTrigger value="builder" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
              <Edit className="h-4 w-4 mr-2" />
              Builder
            </TabsTrigger>
          )}
          <TabsTrigger value="library" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            Library
          </TabsTrigger>
          <TabsTrigger value="executions" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <CheckSquare className="h-4 w-4 mr-2" />
            Executions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="mt-4 space-y-4">
          {/* Schedule-M Compliance Status Live Box */}
          <ReportSection
            id="compliance-status-live"
            title="Schedule-M Compliance Status"
            description="Real-time compliance monitoring and reporting"
          >
            <Card className={`bg-emerald-50 border-2 border-emerald-300 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <CardContent className="p-4 sm:p-8 text-center">
                <h3 className="text-lg font-medium text-slate-800 mb-2">
                  Schedule-M Compliance Status{' '}
                  <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-300 text-sm font-semibold ml-1">
                    Live
                  </span>
                </h3>
                <p className="text-sm text-slate-600 mb-6">
                  Get real-time insights into your compliance readiness with comprehensive metrics, execution status, and regulatory alignment. Generate executive-ready reports for CXO review and regulatory submissions.
                </p>
                <div className="flex justify-center w-full px-0">
                  <Button
                    onClick={handleGenerateCXOReport}
                    disabled={isGeneratingCXO}
                    className="bg-[#fd4816] hover:bg-[#fd4816]/90 text-white relative overflow-hidden text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-2 flex items-center justify-center gap-1.5 sm:gap-2 w-full sm:w-auto min-w-0"
                  >
                    <FileDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="text-center leading-tight flex-1 min-w-0">
                      <span className="block sm:inline">Download Your</span>
                      <span className="block sm:inline sm:ml-1">Compliance Readiness Report</span>
                    </span>
                    <span className="relative inline-flex items-center flex-shrink-0">
                      <Circle className="h-3.5 w-3.5 sm:h-4 sm:w-4 fill-white text-white cxo-glow-icon" />
                      <span className="absolute inset-0 rounded-full bg-white cxo-attention-badge opacity-75"></span>
                    </span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </ReportSection>

          {/* Checklist Builder Box */}
          <ReportSection
            id="checklists-builder"
            title="Checklist Builder"
            description="Template creation and question configuration"
          >
            {isReadOnly ? (
              <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
                <TraceLabel prd="PRD §6.2" req="SM-CL-001" />
                <CardContent className="p-8 text-center">
                  <Edit className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                  <h3 className="text-lg font-medium text-slate-800 mb-2">Read-only Access</h3>
                  <p className="text-sm text-slate-600">
                    External Auditor role cannot create or edit checklist templates.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {!selectedTemplateId && (
                  <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
                    <TraceLabel prd="PRD §6.2" req="SM-CL-001" />
                    <CardContent className="p-8 text-center">
                      <Edit className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                      <h3 className="text-lg font-medium text-slate-800 mb-2">Checklist Builder</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Create and configure checklist templates with question blocks, conditional rules, and deviation triggers.
                      </p>
                      <Button 
                        className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
                        onClick={() => setCreateDialogOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Template
                      </Button>
                    </CardContent>
                  </Card>
                )}
                {selectedTemplateId && (
                  <div className="relative">
                    <TraceLabel prd="PRD §6.2" req="SM-CL-002" />
                    <ChecklistBuilderPanel 
                      templateId={selectedTemplateId} 
                      onTemplateChange={() => {
                        // Refresh if needed
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </ReportSection>
        </TabsContent>

        <TabsContent value="library" className="mt-4">
          <ReportSection
            id="checklists-library"
            title="Checklist Library"
            description="Template library with version control and status"
          >
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.2" req="SM-CL-003" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Template Name</TableHead>
                  <TableHead className="text-slate-600">Version</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Questions</TableHead>
                  <TableHead className="text-slate-600">Category</TableHead>
                  <TableHead className="text-slate-600">Effective</TableHead>
                  <TableHead className="text-slate-600">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No templates found
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map(template => (
                    <TableRow key={template.id} className="hover:bg-slate-50" data-testid={`template-row-${template.id}`}>
                      <TableCell className="font-mono text-xs text-[#fd4816]">{template.id}</TableCell>
                      <TableCell className="text-sm text-slate-800 font-medium">{template.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">v{template.version}</TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(template.status)} text-xs`}>{template.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{template.questions || template.questionsList?.length || 0}</TableCell>
                      <TableCell className="text-sm text-slate-600">{template.category}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {template.effectiveDate ? formatDate(template.effectiveDate) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                            onClick={() => handleView(template)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!isReadOnly && (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                                onClick={() => handleEdit(template)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" className="text-slate-500 hover:text-slate-800 hover:bg-slate-100">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                  <DropdownMenuItem 
                                    onClick={() => handleDuplicate(template.id)}
                                    className="cursor-pointer"
                                  >
                                    <Copy className="h-4 w-4 mr-2" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  {template.status === 'Effective' && (
                                    <DropdownMenuItem 
                                      onClick={() => handleRetire(template.id)}
                                      className="cursor-pointer text-red-600"
                                    >
                                      <Archive className="h-4 w-4 mr-2" />
                                      Retire
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </Card>
          </ReportSection>
          {currentRole.id === 'QA_HEAD' && (
            <div className="mt-4 flex justify-end">
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetDemoData}
                className="border-slate-200 text-slate-600"
              >
                Reset Demo Data
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="executions" className="mt-4">
          <ReportSection
            id="checklists-executions"
            title="Checklist Executions"
            description="Execution history and status tracking"
          >
            <div className="relative">
              <TraceLabel prd="PRD §6.3" req="SM-CL-004" />
              <ExecutionsTable 
                executions={executions} 
                onStartExecution={handleStartExecution}
              />
            </div>
          </ReportSection>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <TemplateCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />

      {/* Viewer Drawer */}
      <TemplateViewerDrawer
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        template={selectedTemplate}
      />
    </div>
  );
};

export default ChecklistsPage;
